import * as React from "react";
import styled from "@emotion/styled";
import {
  Alert,
  Box,
  Button,
  Callout,
  Checkbox,
  Container,
  FadeIn,
  FlexRow,
  H2,
  H3,
  IconArrow,
  IconCheck,
  IconCheckRounded,
  IconChevron,
  IconSearch,
  IconStar,
  IconX,
  Input,
  Label,
  Link,
  Modal,
  ModalMedia,
  PlaceholderBlock,
  Stack,
  Text,
  UnexpectedError,
} from "atoms";
import { constants } from "config";
import { GenericImage, Video } from "features/images";
import { LayoutCuration } from "features/layouts";
import { useScrollProgress } from "utils/hooks";
import { useOpenCallCommunityVoteForm } from "../api/useOpenCallCommunityVoteForm";
import { useOpenCallCurationForm } from "../api/useOpenCallCurationForm";
import type { OpenCallSubmission, OpenCallVoteStep } from "../types";
import { OpenCallSubmissionCard } from "./OpenCallSubmissionCard";
import { useOpenCall } from "../api/useOpenCall";
import { OpenCallVotingSuccessTasks } from "./OpenCallVotingSuccessTasks";

// Percent of grid to scroll through before seeing button in footer
const SCROLL_THRESHOLD = 97;
// Approx. height of fixed footer to offset scroll position
const FOOTER_HEIGHT = 80;

type VoteHookReturn =
  | ReturnType<typeof useOpenCallCommunityVoteForm>
  | ReturnType<typeof useOpenCallCurationForm>;

type OpenCallVoteFormProps = {
  openCallId: string;
  numWinners: number;
  /** Override step and show success message */
  userHasVoted: boolean;
  /** Is currently partner curation as opposed to community voting */
  isPartnerCuration?: boolean;
} & VoteHookReturn;

function OpenCallVoteForm({
  openCallId,
  numWinners,
  userHasVoted,
  isPartnerCuration = false,
  register,
  setValue,
  mutation,
  query,
  onSubmit,
  step,
  setStep,
  selections,
  visibleSubmissions,
  ...props
}: OpenCallVoteFormProps) {
  const { data: openCall } = useOpenCall(openCallId);

  // Attach ref to submission grid to track scrolling progress during selection
  const { scrollRef, progress } =
    useScrollProgress<HTMLDivElement>(FOOTER_HEIGHT);

  // Create handlers for dialog to confirm removing a selection
  const [removeConfirmationId, setRemoveConfirmationId] = React.useState<
    string | null
  >(null);
  const createRemoveConfirmationHandler = (id: string) => () =>
    setRemoveConfirmationId(id);
  const cancelRemove = () => setRemoveConfirmationId(null);

  // Create handler for navigating through voting steps
  const createStepHandler = (s: OpenCallVoteStep) => () => {
    cancelRemove();
    setStep(s);
  };

  // Create handlers for toggling submission modal
  const [isOpen, setIsOpen] = React.useState<number>();
  const createModalOpener = (index: number) => () => setIsOpen(index);
  const closeModal = () => setIsOpen(undefined);

  const numSelections = selections.length;

  if (userHasVoted || step === "done") {
    return (
      <Container width="lg">
        <Success type="success">
          <FlexRow
            justifyContent="center"
            flexDirection="column"
            alignItems="center"
          >
            <SIconCheck />
            <Text
              textAlign="center"
              size="lg"
              font="display"
              textTransform="uppercase"
              bold
            >
              {isPartnerCuration
                ? "Your selection has been received!"
                : "Hurray, you voted!"}
            </Text>

            {isPartnerCuration ? (
              <Text size="xs" textAlign="center">
                Thank you for partnering with us on {openCall?.title}. Our team
                is currently reviewing your selected artworks, and will be in
                touch shortly for next steps. For queries, please email{" "}
                <Link href={`mailto:${constants.supportEmail}`}>
                  {constants.supportEmail}
                </Link>
                .
              </Text>
            ) : (
              <>
                <Text size="xs" textAlign="center">
                  Thank you for helping us to curate {openCall?.title}. Stay
                  tuned to see who gets selected.
                </Text>

                <Link to="/profile/your-actions" variant="secondary">
                  See Your Voting & Curation Tasks
                </Link>
              </>
            )}
          </FlexRow>
        </Success>

        {!isPartnerCuration && <OpenCallVotingSuccessTasks />}
      </Container>
    );
  }

  // Default: step === "selecting"
  let heading = <>Step 1: Select your favorites</>;
  let message = (
    <Text size="xs">
      Select as many artworks as you like during this step. You will be able
      review your favorites in Step 2 and narrow them down to {numWinners}{" "}
      artwork
      {numWinners > 1 && "s"} for submission.
    </Text>
  );
  let showAction = progress > SCROLL_THRESHOLD;
  let footer: JSX.Element | null = (
    <VotingFooter>
      {!!visibleSubmissions.length && (
        <ProgressWrapper>
          <ProgressBar style={{ width: `${progress}%` }} />
        </ProgressWrapper>
      )}

      <AmountSelected>
        {numSelections} <small>Selected</small>
      </AmountSelected>

      <FlexRow gap="0">
        {showAction ? (
          <>
            {numSelections > 0 ? (
              <Button size="xs" onClick={createStepHandler("finalizing")}>
                Review Your Selection
              </Button>
            ) : (
              <>A selection is required to continue</>
            )}
          </>
        ) : (
          <>Scroll down to continue selecting</>
        )}
      </FlexRow>
    </VotingFooter>
  );

  if (step === "finalizing") {
    // Submit will show when partner selects exact amount of winners, or if community curation,
    // when selections is not over amount of winners
    showAction = isPartnerCuration
      ? numSelections === numWinners
      : numSelections <= numWinners;
    const isOver = numSelections - numWinners > 0;
    const numToChange = Math.abs(numSelections - numWinners);
    heading = <>Step 2: Finalize selection</>;
    message = (
      <Text size="xs">
        Remove artworks to narrow your selections down, or go back a step to add
        more. Once you are satisfied, finalize your selection.
      </Text>
    );
    footer = (
      <>
        {mutation.error && <UnexpectedError error={mutation.error} />}

        <VotingFooter isInvalid={!showAction}>
          <AmountSelected>
            {numSelections} / {numWinners} <small>Selected</small>
          </AmountSelected>

          <FlexRow gap="0">
            <FlexRow gap="0 1rem">
              <Button
                variant="blank"
                onClick={createStepHandler("selecting")}
                size="xs"
              >
                <IconArrow dir="l" /> Select More
              </Button>
              {showAction ? (
                <>
                  {numSelections > 0 ? (
                    <Button
                      size="xs"
                      type="submit"
                      disabled={mutation.isLoading}
                    >
                      {isPartnerCuration ? "Finalize Selection" : "Submit Vote"}
                    </Button>
                  ) : (
                    <>A selection is required to continue</>
                  )}
                </>
              ) : (
                <span>
                  {isOver && isPartnerCuration && "Remove "}
                  {isOver && !isPartnerCuration && "Remove at least "}
                  {!isOver && "Add "}
                  <strong>{numToChange}</strong> to continue
                </span>
              )}
            </FlexRow>
          </FlexRow>
        </VotingFooter>
      </>
    );
  }

  let isCommunityVote = false;
  let searchVal = "";
  let setSearchVal: React.Dispatch<React.SetStateAction<string>> = noop;
  if ("search" in props) {
    isCommunityVote = true;
    searchVal = props.search.searchVal;
    setSearchVal = props.search.setSearchVal;
  }

  const createVoteHandler = (id: string) => () => {
    setValue(
      "submissionIds",
      selections.includes(id)
        ? selections.filter((selection) => selection !== id)
        : [...selections, id]
    );
  };

  const createRemoveHandler = (id: string) => () => {
    setValue(
      "submissionIds",
      selections.filter((s) => s !== id)
    );
    closeModal();
  };

  const modalSubmission =
    isOpen === undefined ? undefined : visibleSubmissions[isOpen];
  const modalSubIsInSelections =
    modalSubmission && selections.includes(modalSubmission.submissionId);
  const prevIndex = isOpen !== undefined && isOpen > 0 ? isOpen - 1 : null;
  const nextIndex =
    isOpen !== undefined && isOpen < visibleSubmissions.length - 1
      ? isOpen + 1
      : null;
  const seePrevItem = prevIndex === null ? noop : () => setIsOpen(prevIndex);
  const seeNextItem = nextIndex === null ? noop : () => setIsOpen(nextIndex);

  return (
    <form onSubmit={onSubmit}>
      <SLayoutCuration
        intro={
          <Container width="lg">
            <Stack>
              <H2 size="h3" id="community-vote-heading">
                {heading}
              </H2>
              <div>{message}</div>
            </Stack>
          </Container>
        }
        body={
          (step === "finalizing" || step === "selecting") && query.isLoading ? (
            <SubmissionPlaceholders />
          ) : (
            <>
              {isCommunityVote && step !== "finalizing" && (
                <Container width="lg">
                  <SearchRow gap="10px">
                    <Label>
                      Search by artist name or artwork title
                      <SearchInput
                        onChange={(e) => setSearchVal(e.target.value)}
                        value={searchVal}
                        // Prevents Enter key from submitting the form
                        onKeyPress={(
                          e: React.KeyboardEvent<
                            HTMLInputElement | HTMLTextAreaElement
                          >
                        ) => {
                          e.key === "Enter" && e.preventDefault();
                        }}
                      />
                      <VotingSearchIcon />
                    </Label>

                    {searchVal && (
                      <ClearButton
                        variant="text"
                        size="xxs"
                        onClick={() => setSearchVal("")}
                      >
                        <RedText as="span" bold>
                          <small>
                            <IconX />
                          </small>{" "}
                          Reset search
                        </RedText>
                      </ClearButton>
                    )}
                  </SearchRow>
                </Container>
              )}

              {visibleSubmissions.length === 0 &&
              searchVal &&
              step !== "finalizing" ? (
                <Container width="lg">
                  <FadeIn delay={300}>
                    <NoSearchResults>
                      <Stack gap="sm">
                        <H3 textAlign="center">
                          No submissions matching &ldquo;{searchVal}&rdquo;
                        </H3>
                        <Text textAlign="center">
                          <Button
                            variant="primary"
                            onClick={() => setSearchVal("")}
                          >
                            Reset Search
                          </Button>
                        </Text>
                      </Stack>
                    </NoSearchResults>
                  </FadeIn>
                </Container>
              ) : (
                <SubmissionGrid ref={scrollRef}>
                  {visibleSubmissions.map((sub: OpenCallSubmission, index) => (
                    <SOpenCallSubmissionCard
                      key={sub.submissionId}
                      submission={sub}
                      onClick={createModalOpener(index)}
                    >
                      {openCall?.showPartnerCommunityVotes &&
                        sub.isCommunityFavorite && <Star />}
                      <VoteCheckbox
                        label={sub.title}
                        {...register("submissionIds")}
                        value={sub.submissionId}
                        isHidden={step === "finalizing"}
                      />

                      {step === "finalizing" && (
                        <VoteCheckbox
                          label={sub.title}
                          aria-label={`Remove ${sub.title}`}
                          onChange={createRemoveConfirmationHandler(
                            sub.submissionId
                          )}
                          value={sub.submissionId}
                          checked
                          disabled={removeConfirmationId === sub.submissionId}
                        />
                      )}

                      {removeConfirmationId === sub.submissionId && (
                        <DeletePrompt
                          removeSelection={createRemoveHandler(
                            sub.submissionId
                          )}
                          cancel={cancelRemove}
                        />
                      )}
                    </SOpenCallSubmissionCard>
                  ))}
                </SubmissionGrid>
              )}

              {modalSubmission && (
                <Modal onClose={closeModal}>
                  <Modal.Header>
                    <Stack gap="xxs">
                      <FlexRow
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap="wrap"
                      >
                        <Text size="sm">
                          <strong>{modalSubmission.title}</strong>

                          {!openCall?.isBlindPartnerCuration && (
                            <>
                              {" by "}
                              {modalSubmission.artistId ? (
                                <Link
                                  to={`/artists/${modalSubmission.artistId}`}
                                  target="_blank"
                                >
                                  {modalSubmission.name}
                                </Link>
                              ) : (
                                modalSubmission.name
                              )}
                            </>
                          )}

                          <br />

                          {modalSubmission.countryOfResidence && (
                            <Text size="xs" as="span">
                              {modalSubmission.countryOfResidence}
                            </Text>
                          )}
                        </Text>

                        {openCall?.showPartnerCommunityVotes &&
                          modalSubmission.isCommunityFavorite && (
                            <FavoriteBadge>
                              <IconStar size="16px" aria-hidden /> Community
                              Favorite
                            </FavoriteBadge>
                          )}
                      </FlexRow>
                    </Stack>
                  </Modal.Header>

                  <ModalMedia>
                    {modalSubmission.submission.videoUrl ? (
                      <Video src={modalSubmission.submission} />
                    ) : (
                      <GenericImage
                        src={modalSubmission.submission}
                        sizes="(max-width: 500px) 300px, 1200px"
                      />
                    )}
                  </ModalMedia>

                  {openCall?.voterSubmissionPrompt &&
                    modalSubmission?.voterSubmissionPromptResponse && (
                      <Container width="xs">
                        <Stack>
                          <PromptQ size="sm">
                            {openCall?.voterSubmissionPrompt}
                          </PromptQ>
                          <Text size="xs">
                            {modalSubmission?.voterSubmissionPromptResponse}
                          </Text>
                        </Stack>
                      </Container>
                    )}

                  {isPartnerCuration && openCall?.submissionPrompt && (
                    <Container width="xs">
                      <Stack>
                        <PromptQ size="sm">
                          {openCall?.submissionPrompt}
                        </PromptQ>
                        <Text size="xs">
                          {modalSubmission?.submissionPromptResponse}
                        </Text>
                      </Stack>
                    </Container>
                  )}

                  <Modal.Footer>
                    <FlexRow
                      justifyContent="space-between"
                      alignItems="center"
                      gap="10px"
                      style={{ width: "100%" }}
                    >
                      <Button
                        type="button"
                        size="xs"
                        variant={
                          modalSubIsInSelections ? "primary" : "secondary"
                        }
                        onClick={
                          step === "finalizing"
                            ? createRemoveConfirmationHandler(
                                modalSubmission.submissionId
                              )
                            : createVoteHandler(modalSubmission.submissionId)
                        }
                      >
                        {modalSubIsInSelections ? (
                          <>
                            <IconCheckRounded /> Selected
                          </>
                        ) : (
                          "Select Artwork"
                        )}
                      </Button>

                      <PrevButton
                        type="button"
                        size="xs"
                        variant="default"
                        disabled={prevIndex === null}
                        onClick={seePrevItem}
                        style={{
                          paddingLeft: 6,
                          paddingRight: 12,
                          gap: 3,
                          fontFamily: "GT Walsheim",
                        }}
                      >
                        <IconChevron /> Prev
                      </PrevButton>

                      <NextButton
                        type="button"
                        size="xs"
                        variant="default"
                        disabled={nextIndex === null}
                        onClick={seeNextItem}
                        style={{
                          paddingLeft: 12,
                          paddingRight: 6,
                          gap: 3,
                          fontFamily: "GT Walsheim",
                        }}
                      >
                        Next <IconChevron />
                      </NextButton>
                    </FlexRow>
                    {removeConfirmationId === modalSubmission.submissionId && (
                      <DeletePrompt
                        removeSelection={createRemoveHandler(
                          modalSubmission.submissionId
                        )}
                        cancel={cancelRemove}
                      />
                    )}
                  </Modal.Footer>
                </Modal>
              )}
            </>
          )
        }
        footer={footer}
        showFooter
      />
    </form>
  );
}

function DeletePrompt({
  removeSelection,
  cancel,
}: {
  removeSelection: () => void;
  cancel: () => void;
}) {
  return (
    <>
      <DeletePromptPopup>
        <Stack>
          <Text bold textAlign="center" size="xs">
            Remove this selection?
          </Text>

          <FlexRow gap="10px" justifyContent="center">
            <Button size="xxs" onClick={removeSelection}>
              Remove
            </Button>

            <Button size="xxs" variant="secondary" onClick={cancel}>
              Cancel
            </Button>
          </FlexRow>
        </Stack>
      </DeletePromptPopup>
      <DeletePromptScrim onClick={cancel} aria-hidden>
        Cancel
      </DeletePromptScrim>
    </>
  );
}

const placeholderGrid = Array.from({ length: 12 }).map((_, index) => index);

function SubmissionPlaceholders() {
  return (
    <SubmissionGrid>
      {placeholderGrid.map((key) => (
        <PlaceholderBlock
          width="100%"
          height="auto"
          animated
          style={{ aspectRatio: ".8" }}
          key={key}
        />
      ))}
    </SubmissionGrid>
  );
}

const SLayoutCuration = styled(LayoutCuration)({
  marginTop: "2rem",
});

const VotingFooter = styled.footer<{ isInvalid?: boolean }>(
  ({ theme, isInvalid = false }) => ({
    color: isInvalid ? theme.colors.error : theme.colors.fg,
    display: "flex",
    lineHeight: 1.1,
    fontSize: 15,
    gap: "1rem",
    flexWrap: "wrap",
    justifyContent: "space-between",
    minHeight: 35,
  })
);

const AmountSelected = styled(FlexRow)(({ theme }) => ({
  ...theme.fonts.display,
  gap: 5,
  fontSize: 20,
  flex: "1 0 auto",
  color: "inherit",
  textTransform: "uppercase",
}));

const ProgressWrapper = styled.div({
  height: 9,
  position: "absolute",
  // Overrides padding on parent element
  top: "-1rem",
  left: "50%",
  width: "100vw",
  transform: "translateX(-50%)",
  overflow: "hidden",
});

const ProgressBar = styled.div(({ theme }) => ({
  backgroundColor: theme.colors.accent1,
  height: 9,
  left: 0,
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    display: "block",
    backgroundColor: theme.colors.accent1,
    height: 9,
    width: 10,
    right: -6,
    borderRadius: theme.borderRadius.round,
  },
}));

const SubmissionGrid = styled(Container)(({ theme }) => ({
  display: "grid",
  gap: theme.spacing.gap,
  gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
  minHeight: 100,
  [theme.breakpoints.xs]: {
    gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
  },
  marginTop: "1.5rem",
  marginBottom: "3rem",
}));
SubmissionGrid.defaultProps = {
  width: "lg",
};

const SOpenCallSubmissionCard = styled(OpenCallSubmissionCard)(({ theme }) => ({
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: theme.boxShadow.dark,
  },
}));

const StarWrapper = styled.div(({ theme }) => ({
  backgroundColor: theme.colors.accent3LL,
  borderBottom: `6px solid ${theme.colors.white}`,
  borderLeft: `14px solid ${theme.colors.accent3LL}`,
  borderRight: `14px solid ${theme.colors.accent3LL}`,
  color: theme.colors.accent3,
  fontSize: theme.fontSizes.sm,
  height: 37,
  justifyContent: "center",
  paddingTop: 7,
  position: "absolute",
  right: "1.5rem",
  top: 0,
  width: 28,
  zIndex: 3,
  svg: {
    fontSize: 19,
    flex: "0 0 19px",
    filter: "brightness(.95)",
  },
  "&,*": { transition: ".2s ease all" },
  "&:hover": {
    width: "9em",
    borderBottom: 0,
    borderRadius: `0 0 ${theme.borderRadius.sm} ${theme.borderRadius.sm}`,
  },
}));

const StarRow = styled(FlexRow)(({ theme }) => ({
  left: 0,
  position: "absolute",
  transform: "translateX(-50%)",
  width: 19,
  overflow: "hidden",
  ".communityFavorite:hover &": { left: "50%", width: "7.5em" },
}));

const StarLabel = styled(FadeIn)(({ theme }) => ({
  ...theme.fonts.body,
  color: theme.colors.fg,
  display: "none",
  fontSize: 12,
  fontWeight: "normal",
  margin: 0,
  opacity: 0,
  whiteSpace: "nowrap",
  transform: "translateY(2px)",
  transitionDelay: ".1s",
  ".communityFavorite:hover &": {
    display: "inline-block",
    opacity: 1,
  },
}));

function Star() {
  return (
    <StarWrapper
      aria-label="A Community Favorite"
      className="communityFavorite"
    >
      <StarRow gap="6px" itemsFlex="0 0 auto" flexWrap="nowrap" aria-hidden>
        <IconStar /> <StarLabel>Community Favorite</StarLabel>
      </StarRow>
    </StarWrapper>
  );
}

const VoteCheckbox = styled(Checkbox)<{ isHidden?: boolean }>(
  ({ theme, isHidden = false }) => ({
    display: isHidden ? "none" : "flex",
    fontFamily: theme.fonts.display.fontFamily,
    color: theme.colors.fg,
    gap: 6,
    "&>div": {
      lineClamp: "2",
      overflow: "hidden",
      display: "-webkit-box",
      WebkitLineClamp: "2",
      WebkitBoxOrient: "vertical",
    },
    lineHeight: 1.15,
    fontSize: 14,
    position: "relative",
    textAlign: "left",
    wordBreak: "break-word",
    hyphens: "auto",
    hyphenateLimitChars: 30,
    input: {
      borderRadius: theme.borderRadius.round,
      "&::before": {
        transform: "none",
      },
    },
    "&::after": {
      borderRadius: theme.borderRadius.sm,
      content: '" "',
      display: "block",
      position: "absolute",
      top: -10,
      right: -10,
      bottom: -10,
      left: -10,
      zIndex: 2,
    },
  })
);

const DeletePromptPopup = styled(Box)(({ theme }) => ({
  padding: "1rem",
  position: "absolute",
  left: 10,
  bottom: 10,
  right: 10,
  zIndex: 11,
  p: { color: theme.colors.fg },
})).withComponent(FadeIn);

const DeletePromptScrim = styled(Button)({
  position: "absolute",
  opacity: 0,
  zIndex: 10,
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
});

const Success = styled(Alert)({
  padding: "2rem",
  margin: "2rem auto 3rem",
  display: "flex",
  justifyContent: "center",
  p: { margin: 0 },
});

const SIconCheck = styled(IconCheck)(({ theme }) => ({
  borderRadius: theme.borderRadius.round,
  border: "2px solid",
  borderColor: theme.colors.success,
  color: theme.colors.success,
  padding: 3,
  width: "1.8em",
  height: "1.8em",
  flex: "0 0 1.8em",
}));
const NavButton = styled(Button)(({ theme }) => ({
  paddingTop: ".45em",
  paddingBottom: ".4em",
  background: theme.colors.bg,
  border: "2px solid white",
  boxShadow: theme.boxShadow.light,
  gap: 3,
  "&:hover, &:focus-visible": {
    background: theme.colors.bg,
    boxShadow: theme.boxShadow.dark,
  },
}));

const PrevButton = styled(NavButton)({
  order: -1,
  paddingLeft: 6,
  paddingRight: 12,
  svg: {
    transform: "rotate(-90deg)",
  },
});
const NextButton = styled(NavButton)({
  order: 2,
  paddingLeft: 12,
  paddingRight: 6,
  svg: {
    transform: "rotate(90deg)",
  },
});

const PromptQ = styled(H3)(({ theme }) => ({
  borderBottom: "1px solid",
  borderBottomColor: theme.colors.fg20,
  paddingBottom: 6,
}));

const SearchRow = styled(FlexRow)({
  marginTop: "2rem",
  position: "relative",
});

const SearchInput = styled(Input)(({ theme }) => ({
  borderRadius: theme.borderRadius.round,
  border: `2px solid ${theme.colors.fg}`,
  position: "relative",
  margin: 0,
  display: "flex",
  alignItems: "center",
  lineHeight: 1.6,
  fontSize: 16,
  maxHeight: 40,
  paddingLeft: "2.25rem",
  "&::placeholder": {
    color: theme.colors.fg80,
    opacity: 1,
  },
  "@media (prefers-reduced-motion: no-preference)": {
    transitionDuration: ".2s",
    transitionTimingFunction: "ease",
    transitionProperty: "left",
  },
  [theme.breakpoints.desktop]: {
    height: 36,
    minHeight: 36,
    margin: 0,
    lineHeight: 1,
    paddingTop: 2,
    paddingBottom: 0,
  },
}));

const ClearButton = styled(Button)(({ theme }) => ({
  marginTop: "1.2rem",
  textDecoration: "none",
  borderBottom: "1px solid rgba(0 0 0 / 0)",
  "&&:hover, &&:focus-visible": {
    textDecoration: "none",
    borderBottom: "1px solid currentColor",
    color: theme.colors.fg,
  },
}));

const NoSearchResults = styled(Callout)({
  margin: "1.5rem 0",
  minHeight: "30vh",
  display: "grid",
  placeItems: "center",
});

const VotingSearchIcon = styled(IconSearch)({
  bottom: 10,
  fontSize: 16,
  left: "1em",
  opacity: 0.66,
  pointerEvents: "none",
  position: "absolute",
  zIndex: 3,
});

const RedText = styled(Text)(({ theme }) => ({
  color: theme.colors.accent4,
}));

const FavoriteBadge = styled.span(({ theme }) => ({
  alignItems: "center",
  background: theme.colors.accent3LL,
  borderRadius: theme.borderRadius.sm,
  color: theme.colors.fg,
  display: "flex",
  fontSize: 12,
  gap: 6,
  lineHeight: 1,
  padding: `5px 0.5rem 4px 0.5rem`,
  svg: {
    color: theme.colors.accent3,
    fontSize: 14,
    transform: "translateY(-1px)",
    filter: "brightness(.95)",
  },
}));

export { OpenCallVoteForm };
